import React from 'react'
import Animated from 'react-native-reanimated'
import { ErrorBoundary } from 'react-error-boundary'
import { Trans } from '@lingui/react/macro'

import { View, YStack, useMedia, useThemeName } from 'tamagui'

import { GuildCard } from '@newComponents/Guild/GuildCard'

import { HomeScreenGradient } from '../HomeScreenGradient'

import {
  HeroMainText,
  HeroText,
  HeroCopyText,
  MarkerExtrasText,
  FILLER_GRADIENT_BACKGROUND,
  getPurpleBoxShadow,
} from './styles'
import { selectMediaValue, fontLineHeight } from './utils'
import { CallToActionButton } from './CallToActionButton'
import { HeroAnimation } from './useHeroAnimation'
import { UnauthenticatedHomeScreen_query$data } from './__generated__/UnauthenticatedHomeScreen_query.graphql'

export function MobileHeroSection({
  data,
  animation,
}: {
  data: UnauthenticatedHomeScreen_query$data
  animation: HeroAnimation
}) {
  const media = useMedia()
  const isDark = useThemeName() === 'dark'
  const {
    gradientContainerRef,
    guildCardRef,
    gradient,
    setGradient,
    cardStyle,
  } = animation

  const mobileHeroLineHeight = selectMediaValue(media, [
    fontLineHeight(12) + 12,
    fontLineHeight(14) - 4,
  ])

  return (
    <View position='relative' width='100%' overflow='hidden'>
      {/* Static gradient background - fallback while WebGL canvas loads */}
      <View
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={0}
        style={{
          backgroundImage: `url(${FILLER_GRADIENT_BACKGROUND})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      />
      <View
        ref={gradientContainerRef}
        position='relative'
        zIndex={1}
        width='100%'
        style={{
          boxShadow: getPurpleBoxShadow(isDark),
        }}
        onLayout={() => {
          if (gradient) {
            gradient.resize()
          }
        }}
      >
        <ErrorBoundary
          fallback={<View height={selectMediaValue(media, [600, 650, 950])} />}
        >
          <HomeScreenGradient
            getGradient={(gradient) => setGradient(gradient)}
            height={600}
            borderTopLeftRadius={0}
            borderTopRightRadius={0}
            $gtXs={{
              height: 650,
            }}
            $gtSm={{
              height: 950,
            }}
          />
        </ErrorBoundary>
      </View>

      <View
        position='absolute'
        top={0}
        width='100%'
        height='100%'
        paddingTop={100}
        paddingHorizontal={40}
        zIndex={2}
        style={{ mixBlendMode: 'overlay' }}
      >
        <HeroMainText size='$13' $gtXs={{ size: '$15' }} textAlign='center'>
          <Trans>ELEVATE</Trans>
        </HeroMainText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        top={selectMediaValue(media, [150, 190])}
        left={selectMediaValue(media, [40, 105])}
        zIndex={2}
        style={{
          mixBlendMode: 'overlay',
          transform: 'rotate(20deg)',
        }}
      >
        <MarkerExtrasText
          size='$7'
          $gtXs={{ size: '$8' }}
          $gtSm={{ size: '$9' }}
          $gtMd={{ size: '$10' }}
          textAlign='center'
        >
          I
        </MarkerExtrasText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        top={selectMediaValue(media, [170, 225])}
        left={selectMediaValue(media, [50, 70])}
        zIndex={2}
        style={{ mixBlendMode: 'overlay' }}
      >
        <MarkerExtrasText size='$14' $gtXs={{ size: '$15' }} textAlign='center'>
          v
        </MarkerExtrasText>
      </View>

      <View
        position='absolute'
        width='100%'
        height='100%'
        top={80 + mobileHeroLineHeight}
        zIndex={2}
      >
        <YStack alignItems='center' gap='$10'>
          <View paddingHorizontal={40}>
            <YStack alignItems='center' gap='$10'>
              <HeroText size='$9' $gtXs={{ size: '$11' }} textAlign='center'>
                <Trans>Your Community</Trans>
              </HeroText>

              <YStack gap='$1' alignItems='center'>
                <HeroCopyText size='$6' textAlign='center'>
                  <Trans>Guild is the platform for communities.</Trans>
                </HeroCopyText>

                <HeroCopyText size='$6' textAlign='center'>
                  <Trans>No matter the shape.</Trans>
                </HeroCopyText>
              </YStack>

              <CallToActionButton />
            </YStack>
          </View>

          {data.nodeBySlugId && 'pastEvents' in data.nodeBySlugId && (
            <View ref={guildCardRef} maxWidth={400} marginTop='$4'>
              <Animated.View style={cardStyle}>
                <GuildCard
                  guild={data.nodeBySlugId}
                  limitDescriptionLines={10}
                  showMoreDialog
                  expand
                />
              </Animated.View>
            </View>
          )}
        </YStack>
      </View>
    </View>
  )
}
